import { RepeatIcon } from '@chakra-ui/icons';
import {
  Box,
  Center,
  HStack,
  IconButton,
  Image,
  SimpleGrid,
  Skeleton,
} from '@chakra-ui/react';
import { useToastWrapper } from 'hooks/useToastWrapper';
import { useCallback, useState } from 'react';

import Icon from 'components/Icon';
import Link from 'components/Link';
import Text from 'components/Text';

import DevSandboxTooltip from '../../DevSandboxTooltip';

const generatePhotoUrls = (refresh: number) => {
  return Array.from({ length: 12 }, (_, i) => {
    const seed = 1000 + i * 7 + refresh * 200;
    return {
      id: `photo-${seed}`,
      url: `https://picsum.photos/seed/${seed}/400/300`,
      fullUrl: `https://picsum.photos/seed/${seed}/1920/1080`,
    };
  });
};

const PhotosTab = () => {
  const [refreshCount, setRefreshCount] = useState(0);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const { toastSuccess } = useToastWrapper();

  const photos = generatePhotoUrls(refreshCount);

  const handleRefresh = useCallback(() => {
    setLoadedImages(new Set());
    setRefreshCount((prev) => prev + 1);
  }, []);

  const handleCopyUrl = useCallback(
    (url: string) => {
      navigator.clipboard.writeText(url);
      toastSuccess('Image URL copied to clipboard!');
    },
    [toastSuccess]
  );

  const handleImageLoad = useCallback((id: string) => {
    setLoadedImages((prev) => new Set(Array.from(prev).concat(id)));
  }, []);

  return (
    <Box>
      {/* Header */}
      <Box mb={4}>
        <HStack spacing={1} alignItems="center" mb={2} width="fit-content">
          <Text fontSize="large" fontWeight="semibold">
            Stock Photos
          </Text>
          <DevSandboxTooltip label="Load new images">
            <IconButton
              aria-label="Refresh images"
              icon={<RepeatIcon />}
              size="xs"
              variant="ghost"
              onClick={handleRefresh}
            />
          </DevSandboxTooltip>
        </HStack>
        <Text fontSize="small" opacity="secondary">
          Click any image to copy its URL. Use in your HTML/CSS.
        </Text>
      </Box>

      {/* Photo grid */}
      <SimpleGrid columns={3} spacing={3}>
        {photos.map((photo) => (
          <Box
            key={photo.id}
            position="relative"
            borderRadius="md"
            overflow="hidden"
            cursor="pointer"
            role="group"
            onClick={() => handleCopyUrl(photo.fullUrl)}
            _hover={{ transform: 'scale(1.02)' }}
            transition="transform 0.15s"
          >
            {!loadedImages.has(photo.id) && (
              <Skeleton height="100px" borderRadius="md" />
            )}
            <Image
              src={photo.url}
              alt="Stock photo"
              width="100%"
              height="100px"
              objectFit="cover"
              display={loadedImages.has(photo.id) ? 'block' : 'none'}
              onLoad={() => handleImageLoad(photo.id)}
              onError={() => handleImageLoad(photo.id)}
            />
            {/* Hover overlay */}
            <Center
              position="absolute"
              inset={0}
              bg="blackAlpha.600"
              opacity={0}
              _groupHover={{ opacity: 1 }}
              transition="opacity 0.15s"
            >
              <Icon type="Copy" />
            </Center>
          </Box>
        ))}
      </SimpleGrid>

      {/* More options */}
      <Box mt={4} pt={4} borderTopWidth="1px" borderColor="whiteAlpha.200">
        <Text fontSize="small" opacity="secondary">
          Need more options?{' '}
          <Link href="https://unsplash.com" isExternal fontSize="small">
            Unsplash
          </Link>
          {' | '}
          <Link href="https://www.pexels.com" isExternal fontSize="small">
            Pexels
          </Link>
          {' | '}
          <Link href="https://pixabay.com" isExternal fontSize="small">
            Pixabay
          </Link>
          {' | '}
          <Link href="https://picsum.photos" isExternal fontSize="small">
            Picsum
          </Link>
        </Text>
      </Box>
    </Box>
  );
};

export default PhotosTab;
